import { graphql } from 'gatsby';
import React, { Suspense } from 'react';

import styled from '@emotion/styled';
import { Box, Container, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2'; // Grid version 2
import { useTheme } from '@mui/material/styles';
import {
  useContentfulInspectorMode, useContentfulLiveUpdates
} from '../shared/hooks/contentful';

import { dynamicHeroHeight } from '../shared/styles/dynamicHeroHeight';
import { renderContentfulRichText } from '../shared/utils';
import { DynamicAdditionalComponent } from './dynamicAdditionalComponent';
import { HorizontalSlantContainer } from './horizontal-slant-container';
import { ButtonList, ContentfulAssetImage, Kicker } from './ui';

export type FeatureProps = Queries.ContentfulHomepageFeature & {
  index: number
}

const HeightDiv = styled.div`
${dynamicHeroHeight({
  autoHeight: true,
})}
`

export default function Feature(props: FeatureProps) {
  const theme = useTheme();
  const inspectorProps = useContentfulInspectorMode();
  const data = useContentfulLiveUpdates({
    ...props,
    sys: { id: props.contentful_id },
  });

  const i = data.index
  const flip = Boolean(i % 2)
  const everySecondFeature = Boolean(i % 2)
  const everyThirdFeature = Boolean(i % 3)
  const bottomDirection = everyThirdFeature ? "right" : "left"
  const topDirection = everyThirdFeature ? "left" : "right"
  const slantContainer = everySecondFeature

  const imageItem = data.image ? (
    <Box
      sx={[{
        pl: 1,
        pr: 1
      }, slantContainer ? {
        pt: 3
      } : {
        pt: 0
      }]}
    >
      <Suspense>
        <ContentfulAssetImage asset={data.image} alt={data.image.title} width={data.image.width?.toString()} height={data.image.height?.toString()}
          contentfulPreviewProps={inspectorProps({
            entryId: props.contentful_id,
            fieldId: 'image',
          })} />
      </Suspense>
    </Box>
  ) : null

  const textItem = (
    <Stack direction="column" gap={2}>
      <Box
        sx={{
          pl: 1,
          pr: 1,
        }}
      >
        {data.kicker && (
          <Kicker
            label={data.kicker}
            background={theme.palette.gradients.light.end}
            icon={data.kickerIcon}
            {...inspectorProps({
              entryId: props.contentful_id,
              fieldId: 'kicker',
            })}
          />
        )}
      </Box>

      <Typography
        variant="h2"
        sx={{
          pl: 1,
          pr: 1,
        }}
        {...inspectorProps({
          entryId: props.contentful_id,
          fieldId: 'heading',
        })}
      >
        {data.heading}
      </Typography>
      <Typography component={"div"} variant="body1" sx={{ pageBreakAfter: props.additionalComponents && props.additionalComponents.length > 0 ? 'always' : undefined }}>
        <Box
          sx={{
            whiteSpace: "pre-wrap",
            pl: 1,
            pr: 1,
            // textAlign: {
            //   xs: "center",
            //   sm: "center",
            //   md: "center",
            //   lg: "inherit",
            // },
          }}
          {...inspectorProps({
            entryId: props.contentful_id,
            fieldId: 'content',
          })}
        >{renderContentfulRichText(data.content)}</Box>

      </Typography>

      {props.additionalComponents && props.additionalComponents.length > 0 ? props.additionalComponents.map(c => <DynamicAdditionalComponent key={c} id="dosage-getting-started" component={c as any} />) : null}
      {data.links ? <ButtonList links={data.links as any} /> : <></>}
    </Stack>
  )

  const containerItem = (
    <Container>
      <Grid
        container
        columnSpacing={{ xs: 0, sm: 0, md: slantContainer ? 4 : 4, lg: 7 }}
        rowSpacing={{ xs: 1, sm: 1, md: 0 }}
        alignItems="center"
        direction={{
          xs: "row-reverse",
          sm: "row-reverse",
          md: flip ? "row" : "row-reverse",
        }}
        sx={{
          height: "100%",
          minHeight: "400px"
        }}
        justifyContent="space-between"
      >
        {imageItem && <Grid
          size={{
            xs: 12,
            sm: 12,
            md: 6,
            lg: 7
          }}>
          <HeightDiv>
            <Box
              sx={[{
                height: "100%",
                textAlign: "center",

                pb: {
                  xs: 0,
                  sm: 0
                }
              }, slantContainer ? {
                pt: {
                  xs: "30px"
                }
              } : {
                pt: {
                  xs: 0
                }
              }, slantContainer ? {
                pt: {
                  sm: "30px"
                }
              } : {
                pt: {
                  sm: 0
                }
              }, slantContainer ? {
                pt: {
                  md: "60px"
                }
              } : {
                pt: {
                  md: 0
                }
              }, slantContainer ? {
                pt: {
                  lg: "60px"
                }
              } : {
                pt: {
                  lg: 0
                }
              }, slantContainer ? {
                pb: {
                  md: "60px"
                }
              } : {
                pb: {
                  md: 0
                }
              }, slantContainer ? {
                pb: {
                  lg: "60px"
                }
              } : {
                pb: {
                  lg: 0
                }
              }]}
            >
              {imageItem}
            </Box>
          </HeightDiv>
        </Grid>}
        <Grid
          size={{
            xs: 12,
            sm: 12,
            md: imageItem ? 6 : 12,
            lg: imageItem ? 5 : 12
          }}>
          <HeightDiv>
            <Box
              sx={[{
                height: "100%",
                pt: {
                  md: 0,
                  lg: 0
                },
                pb: {
                  md: 0,
                  lg: 0
                },
              }, slantContainer ? {
                pt: {
                  xs: "60px"
                }
              } : {
                pt: {
                  xs: 0
                }
              }, slantContainer ? {
                pt: {
                  sm: "60px"
                }
              } : {
                pt: {
                  sm: 0
                }
              }, slantContainer ? {
                pb: {
                  xs: "80px"
                }
              } : {
                pb: {
                  xs: "40px"
                }
              }, slantContainer ? {
                pb: {
                  sm: "80px"
                }
              } : {
                pb: {
                  xs: "40px"
                }
              }]}
            >
              {textItem}
            </Box>
          </HeightDiv>
        </Grid>
      </Grid>
    </Container>
  )

  return slantContainer ? (
    <HorizontalSlantContainer
      id={data.id}
      gradientDirection="vertical"
      bottomDirection={bottomDirection}
      topDirection={topDirection}
      leftColor={theme.palette.gradients.light.start}
      rightColor={theme.palette.gradients.light.end}
      autoHeight
    >
      {containerItem}
    </HorizontalSlantContainer>
  ) : (
    <Box
      sx={{
        mt: { xs: "initial", sm: "initial", md: "initial", lg: "0" },
        mb: { xs: "initial", sm: "initial", md: "initial", lg: "0" },
      }}
    >
      {containerItem}
    </Box>
  )
}

export const query = graphql`
  fragment HomepageFeatureContent on ContentfulHomepageFeature {
    id
    kicker
    __typename
    contentful_id
    additionalComponents
    kickerIcon {
      id
      url
      title
      __typename
      contentful_id
      
      localFile {
        childImageSharp { 
          gatsbyImageData(height: 20, width: 20)
        }
      }
    }
    heading
    content {
      raw
    }
    links {
      id
      href
      text
      __typename
      contentful_id
    }
    image {
      id
      url
      title
      __typename
      contentful_id
      
      localFile {
        childImageSharp { 
          gatsbyImageData(
            placeholder: NONE
            breakpoints: [400, 600]
            width: 600
            quality: 100
            layout: CONSTRAINED
          )
        }
      }
    }
  }
`
